<!--
// app/pages/[region]/PlayersDraft.vue
-->
<script setup lang="ts">
import type { Player } from "~/src/player/domain/Player";
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import type { BannerCard } from "~/src/content/domain/models/Banner";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import CardStoreAd from "~/app/components/Shared/CardStoreAd.vue";
import CardDraftPlayer from "~/app/components/Shared/players/CardDraftPlayer.vue";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import SearchField from "~/app/components/Shared/SearchField.vue";
import CardNoDataMessage from "~/app/components/Shared/CardNoDataMessage.vue";
import PaginationNav from "~/app/components/Shared/PaginationNav.vue";
import ArrowFilterIcon from "~/app/components/Shared/icons/ArrowFilterIcon.vue";
import TeamsFilter from "~/app/components/Shared/TeamsFilter.vue";
import { useSeasons } from "~/app/composables/season/seasons";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useWebContext } from "~/app/composables/webContext";
import { usePagination } from "~/app/composables/pages/pagination";
import bannerUiService from "~/src/content/infrastructure/ui-services/bannerUiService";
import { slugify } from "~/src/Shared/utils";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useRegions } from "~/app/composables/regions";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/draft-players",
    es: "/[region]/jugadores-draft",
    de: "/[region]/draft-spieler",
    fr: "/[region]/joueurs-draft",
    it: "/[region]/giocatori-draft",
    pt: "/[region]/jogadores-draft",
    ar: "/[region]/draft-players",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { t, locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const { getSeasonPlayers } = useSeasons();
const { currentSeason, region } = useCompetitionStore();
const { isNations } = useRegions();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { contextSiteTitle, contextDomain } = useWebContext();
const { currentPage, itemsPerPage, resetPage } = usePagination();
const { getCardBanner } = bannerUiService();

const storeBanner = ref<BannerCard | undefined>();

const seasonPlayers = ref<Player[]>();
const seasonMvpPartner = ref<SeasonStatsPartner>();
const loadingSeasonData = ref<boolean>(false);
const errorMessage = ref<boolean>(false);
const searchInputValue = ref<string | null>(null);
const teamFilterValue = ref<string | number>(0);
const teamsOptions = ref<string[] | undefined>();
const playerCardsWrapper = ref<null | HTMLElement>(null);
const playersWrapperKey = ref<number>(0);
const isMobile = ref<boolean>(false);
const showFilterItems = ref<boolean>(false);

onBeforeMount(() => {
  isMobile.value = window.innerWidth < 1025;
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth < 1025;
  });
});

onMounted(async () => {
  await nextTick();
  storeBanner.value = await getCardBanner("players", locale.value);
});

const getSeasonData = async (): Promise<void> => {
  try {
    loadingSeasonData.value = true;
    errorMessage.value = false;
    teamFilterValue.value = 0;

    const seasonIdValue = seasonId.value;
    let fetchedPlayers: Player[] | undefined;

    if (seasonIdValue) {
      fetchedPlayers = await getSeasonPlayers(seasonIdValue);

      const _teamsOptions: string[] = [];
      fetchedPlayers?.forEach(p => {
        if (p.teams) {
          const team = p.teams[0].completeName;
          const teamExists = _teamsOptions.some(t => t === team);
          if (!teamExists && !!team) _teamsOptions.push(team);
        }
      });

      seasonPlayers.value = fetchedPlayers;
      teamsOptions.value = _teamsOptions.length
        ? _teamsOptions.sort((a, b) => a.localeCompare(b))
        : undefined;

      seasonMvpPartner.value =
        currentSeason && currentSeason.id === seasonIdValue
          ? currentSeason.metaInformation?.stats_partner
          : undefined;
    }
  } catch (e) {
    errorMessage.value = true;
  } finally {
    loadingSeasonData.value = false;
    playersWrapperKey.value++;
  }
};

await getSeasonData();

const handleSeasonChange = async (): Promise<void> => {
  await getSeasonData();
  resetPage();
};

const filteredPlayers = computed((): Player[] | undefined => {
  if (!seasonPlayers.value) return [];

  let _players = seasonPlayers.value;

  if (_players?.length) {
    if (searchInputValue.value) {
      const cleanValue = searchInputValue.value.toLowerCase();
      _players = _players.filter(p => p.shortName.toLowerCase().includes(cleanValue));
    }

    if (teamFilterValue.value !== 0) {
      _players = _players.filter(p => p.teams && p.teams[0].completeName === teamFilterValue.value);
    }
  }

  return _players;
});

const paginatedPlayers = computed((): Player[] | undefined => {
  const startIndex = (currentPage.value - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return filteredPlayers.value?.slice(startIndex, endIndex);
});

const toggleShowFilters = () => {
  if (isMobile.value) {
    showFilterItems.value = !showFilterItems.value;
  }
};

function resetFilters() {
  teamFilterValue.value = 0;
  searchInputValue.value = null;
}

const playersSchema = computed(() => {
  if (!paginatedPlayers.value) return [];

  return paginatedPlayers.value?.map((p, index) => {
    const url = localePath({
      name: "players-playerId-playerName",
      params: {
        playerId: p.id,
        playerName: slugify(p.shortName),
      },
    });

    return `{"@type": "ListItem","position": "${index + 1}","item": {"@type" : "Person","name" : "${p.shortName}", "url":"https://${contextDomain + url}"}}`;
  });
});

useSeoMeta({
  title: t("menuItem.playersDraft"),
  ogTitle: t("menuItem.playersDraft"),
  description: t("meta.description.draftPlayers.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.draftPlayers.page", { league: region.leagueTitle || contextSiteTitle }),
});

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org","@type": "ItemList", "itemListElement": [${playersSchema.value}]}`,
    },
  ],
});
</script>

<template>
  <header-banner-title
    :title="isNations ? $t('menuItem.players') : $t('draftPlayers.banner.title')"
  ></header-banner-title>

  <section class="pb-4 md:py-8 sm:px-4">
    <div class="kql-w-container">
      <div class="px-4 sm:px-0 md:hidden">
        <seasons-filter
          v-model:seasonClusterId="seasonClusterId"
          v-model:seasonId="seasonId"
          @change="handleSeasonChange"
        ></seasons-filter>
      </div>

      <div class="p-4 md:p-0 border-y border-gray-800 md:border-none">
        <div v-if="isMobile" class="flex items-center justify-between">
          <p v-if="filteredPlayers" class="text-gray-600 text-sm">{{ filteredPlayers.length }}</p>
          <div class="min-w-max ml-2" v-if="!!searchInputValue || !!teamFilterValue">
            <button class="text-primary px-3 py-2 text-xs flex items-center" @click="resetFilters">
              <svg
                class="w-2.5 h-2.5 mr-1"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              {{ $t("draft.player.filters.clean") }}
            </button>
          </div>
          <button
            class="text-gray-600 text-sm rounded-lg w-full flex justify-end items-center py-2"
            @click="toggleShowFilters"
          >
            {{ $t("shared.filter.title") }}
            <ArrowFilterIcon :showFilterItems="showFilterItems"></ArrowFilterIcon>
          </button>
        </div>

        <div
          v-show="showFilterItems || !isMobile"
          class="flex flex-col md:flex-row flex-wrap items-stretch justify-center md:items-center md:justify-end gap-x-4 md:px-6"
        >
          <div class="hidden md:block">
            <seasons-filter
              v-model:seasonClusterId="seasonClusterId"
              v-model:seasonId="seasonId"
              @change="handleSeasonChange"
            ></seasons-filter>
          </div>
          <div class="flex items-center justify-between min-w-24 gap-x-4 mt-2 mb-4 md:my-0">
            <TeamsFilter
              v-if="teamsOptions && teamsOptions.length"
              v-model="teamFilterValue"
              :teams-options="teamsOptions"
              @change="resetPage"
            ></TeamsFilter>
          </div>
          <div class="flex-shrink w-full md:w-auto flex items-center justify-center">
            <search-field v-model.trim="searchInputValue" @change="resetPage"></search-field>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-[336px_1fr_1fr] gap-4 px-0 lg:px-6 pb-4 md:py-6">
        <div class="hidden md:flex gap-4 xl:block xl:max-h-none justify-center">
          <div class="min-w-[336px] h-auto block sm:hidden xl:block xl:w-auto xl:h-auto sticky top-32">
            <card-store-ad
              v-if="storeBanner && storeBanner.image"
              light
              :product-image="storeBanner.image"
              :product-title="storeBanner.description"
              :product-link="storeBanner.link"
              :product-link-target="storeBanner.linkTarget"
              :button-text="storeBanner.buttonText"
              title-card="Tienda oficial"
            ></card-store-ad>
          </div>
        </div>
        <div
          class="xl:col-span-2 xl:auto-rows-min grid grid-cols-1 gap-2 bigsm:grid-cols-2 md:gap-4 relative"
          v-if="seasonPlayers"
          ref="playerCardsWrapper"
          :key="playersWrapperKey"
        >
          <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

          <card-no-data-message
            v-if="!seasonPlayers.length"
            :message="$t('shared.errorMessage.noData')"
          ></card-no-data-message>

          <div v-for="(player, i) in paginatedPlayers" :key="player.id">
            <card-draft-player
              :player-complete-name="player.shortName"
              :player-number="player.jersey"
              :player-position="player.role"
              :player-image="player.image"
              :season-id="seasonId"
              :player-id="player.id"
              :player="player"
              :mvp-partner="seasonMvpPartner"
              :player-team="player.teams ? player.teams[0] : undefined"
              :loadingLazy="i > 4"
              with-team
              isPlayersDraft
              :show-clause="!isNations"
            ></card-draft-player>
          </div>

          <card-no-data-message
            v-if="!filteredPlayers?.length && !!seasonPlayers.length"
            :message="$t('shared.filters.noData')"
          ></card-no-data-message>
        </div>
      </div>
    </div>

    <pagination-nav
      v-model="currentPage"
      :total-items-amount="filteredPlayers?.length"
      :items-per-page="itemsPerPage"
      scroll-to-top
    />

    <div class="block px-2 md:hidden w-full max-w-[336px] h-auto mx-auto py-6">
      <card-store-ad
        v-if="storeBanner && storeBanner.image"
        light
        :product-image="storeBanner.image"
        :product-title="storeBanner.description"
        :product-link="storeBanner.link"
        :product-link-target="storeBanner.linkTarget"
        :button-text="storeBanner.buttonText"
        title-card="Tienda oficial"
      ></card-store-ad>
    </div>
  </section>
</template>

<style scoped></style>
